import type { LocationQuery, RouteLocationNormalizedGeneric, RouteLocationRaw } from '#vue-router'
import type { AppRequestRoute } from '@ts-routes/types'
import type { LoginResponse } from '@brighterly/lib-core-types/login/interfaces'

import { useAuthStore } from '@stores/auth'
import { useKidStore } from '@stores/kid'
import { useDisciplineStore } from '@stores/discipline'

import { useCsRoutes } from '@composables/routes/cs-route.hook'
import { useLogOut } from '@composables/pages/login/log-out.hook'
import { useAuthSuccess } from '@composables/pages/login/auth-success.hook'

import axios, { type AxiosResponse } from 'axios'
import { isObject } from '@helpers/object'

export const useMiddlewareAutoLogin = async (to: RouteLocationNormalizedGeneric, signature: string): Promise<RouteLocationRaw | undefined> => {
  const path = to.path

  const authStore = useAuthStore()
  const kidStore = useKidStore()
  const disciplineStore = useDisciplineStore()

  authStore._hydrateFromStorage?.()
  kidStore.hydrateFromStorage?.()
  disciplineStore.hydrateFromStorage?.()

  const {
    logout,
  } = useLogOut()

  await logout({ navigate: false })

  if (!path) {
    const msg = 'useMiddlewareAutoLogin: Invalid path'
    const err = new Error(msg)

    console.error(err)
  } else {
    const customer = (to.query?.customer as string | undefined) ?? undefined
    const discipline = (to.query?.discipline as string | undefined) ?? undefined

    const params: LocationQuery = isObject(to.query)
      ? { ...to.query }
      : {}

    delete params.signature
    delete params.discipline

    const id: string | null = customer
      ? customer
      : discipline
        ? discipline
        : null

    const routeName: AppRequestRoute | null = customer
      ? 'autologin.customer'
      : discipline
        ? 'autologin.discipline'
        : null

    const url = (routeName && id)
      ? useCsRoutes(routeName, {
        '[id]': id,
        signature,
      })
      : null

    if (!url) {
      const msg = 'useMiddlewareAutoLogin: Invalid autologin URL'
      const err = new Error(msg)

      console.error(err)
    } else {
      try {
        const res: AxiosResponse<LoginResponse> = await axios.get(url)
        return await useAuthSuccess({
          redirectTo: path,
          queryParams: params,
          res,
          navigate: false,
        })
      } catch (err) {
        console.error(err)
      }
    }
  }
}
